import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/Icon';

const MANAGER_COLORS = [
  { bg: 'rgba(var(--lor-accent-rgb),0.18)', border: 'var(--lor-accent)', text: 'var(--lor-accent)' },
  { bg: 'rgba(76,175,80,0.18)',   border: '#4CAF50', text: '#4CAF50'  },
  { bg: 'rgba(41,182,246,0.18)',  border: '#29B6F6', text: '#29B6F6'  },
  { bg: 'rgba(255,159,28,0.18)',  border: '#FF9F1C', text: '#FF9F1C'  },
  { bg: 'rgba(226,75,74,0.18)',   border: '#E24B4A', text: '#E24B4A'  },
  { bg: 'rgba(123,104,238,0.18)', border: '#7B68EE', text: '#7B68EE'  },
  { bg: 'rgba(0,188,212,0.18)',   border: '#00BCD4', text: '#00BCD4'  },
  { bg: 'rgba(255,107,157,0.18)', border: '#FF6B9D', text: '#FF6B9D' },
  { bg: 'rgba(102,187,106,0.18)', border: '#66BB6A', text: '#66BB6A' },
  { bg: 'rgba(255,112,67,0.18)',  border: '#FF7043', text: '#FF7043'  },
];

function colorFor(idx) {
  return MANAGER_COLORS[idx % MANAGER_COLORS.length];
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function abbrev(name) {
  if (!name) return '?';
  const trimmed = name.trim();
  const words = trimmed.split(/\s+/);
  if (words.length === 1) return trimmed.slice(0, 6);
  return words.map(w => w[0]).join('').toUpperCase().slice(0, 4);
}

function AvatarCircle({ avatar, name, size = 28 }) {
  return avatar
    ? <img
        src={`https://sleepercdn.com/avatars/thumbs/${avatar}`}
        alt={name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    : <div style={{
        width: size, height: size, borderRadius: '50%', background: '#2a2a2a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}><Icon emoji="👤" size={Math.round(size * 0.45)} /></div>;
}

export default function LORDraftOrder({ api }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${api}/api/lor/draft-order`)
      .then(r => r.ok ? r.json() : r.text().then(t => { throw new Error(t); }))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="lor-loading">Loading draft order…</div>;
  if (error)   return <div className="lor-empty" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!data)   return null;

  const { draft_order = [], total_rounds, total_teams, reversal_round } = data;

  const pickToIdx = {};
  draft_order.forEach((mgr, idx) => {
    (mgr.snake_picks || []).forEach(pick => { pickToIdx[pick] = idx; });
  });

  const pickInRound = (mgr, round) => {
    const picks = mgr.snake_picks || [];
    return picks[round - 1] ?? null;
  };

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📋" size={18} /> 2026 Draft Order</h2>
        <span className="lor-page-meta">
          {total_rounds} rounds · Snake with {ordinal(reversal_round)}-round reversal
        </span>
      </div>

      {/* ── Manager Table ── */}
      <div className="lor-card" style={{ padding: 0, marginBottom: 24 }}>
        <table className="lor-standings-table">
          <thead>
            <tr>
              <th style={{ width: 52 }}>Slot</th>
              <th>Manager</th>
              <th style={{ textAlign: 'center' }}>Round 1</th>
              <th style={{ textAlign: 'center' }}>Round 2</th>
              <th style={{ textAlign: 'center' }}>Round 3</th>
              <th style={{ width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {draft_order.map((mgr, idx) => {
              const c = colorFor(idx);
              return (
                <tr key={mgr.roster_id}>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 30, height: 30, borderRadius: '50%',
                      background: c.bg, border: `1px solid ${c.border}`,
                      fontWeight: 700, color: c.text, fontSize: '0.88rem',
                    }}>
                      {mgr.slot}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AvatarCircle avatar={mgr.avatar} name={mgr.display_name} size={28} />
                      <span style={{ fontWeight: 600, color: '#eee', fontSize: '0.9rem' }}>
                        {mgr.display_name}
                      </span>
                    </div>
                  </td>
                  {[1, 2, 3].map(r => {
                    const pick = pickInRound(mgr, r);
                    return (
                      <td key={r} style={{ textAlign: 'center' }}>
                        {pick != null ? (
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 12px',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            background: c.bg,
                            color: c.text,
                            border: `1px solid ${c.border}50`,
                          }}>
                            #{pick}
                          </span>
                        ) : '—'}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'right', paddingRight: 12 }}>
                    <button
                      onClick={() => navigate('/league-of-record/mock-draft')}
                      style={{
                        padding: '4px 12px', borderRadius: 8, fontSize: '0.75rem',
                        background: 'rgba(var(--lor-accent-rgb),0.08)',
                        border: '1px solid rgba(var(--lor-accent-rgb),0.2)',
                        color: 'var(--lor-accent)', cursor: 'pointer', fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Mock Draft →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Color Legend ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
        {draft_order.map((mgr, idx) => {
          const c = colorFor(idx);
          return (
            <div key={mgr.roster_id} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '3px 10px', borderRadius: 8,
              background: c.bg, border: `1px solid ${c.border}40`,
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: c.border, display: 'inline-block', flexShrink: 0,
              }} />
              <span style={{ fontSize: '0.72rem', color: c.text, fontWeight: 600 }}>
                Slot {mgr.slot} — {mgr.display_name}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Snake Board Grid ── */}
      <div className="lor-card" style={{ padding: '16px 12px' }}>
        <div style={{
          fontSize: '0.72rem', color: '#555', textTransform: 'uppercase',
          letterSpacing: '0.06em', marginBottom: 14, fontWeight: 700,
        }}>
          Full Snake Board — All {(total_rounds || 0) * (total_teams || 0)} Picks
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{
            borderCollapse: 'collapse', width: '100%', minWidth: 580,
            tableLayout: 'fixed',
          }}>
            <thead>
              <tr>
                <th style={{
                  width: 38, padding: '5px 4px', fontSize: '0.62rem',
                  color: '#444', textAlign: 'center',
                  borderBottom: '1px solid #1e1e1e', background: '#0d0d0d',
                }}>
                  Rnd
                </th>
                {Array.from({ length: total_teams }, (_, i) => (
                  <th key={i} style={{
                    padding: '5px 2px', fontSize: '0.62rem',
                    color: '#444', textAlign: 'center',
                    borderBottom: '1px solid #1e1e1e', background: '#0d0d0d',
                  }}>
                    P{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: total_rounds }, (_, rIdx) => {
                const round = rIdx + 1;
                return (
                  <tr
                    key={round}
                    style={{ background: round % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}
                  >
                    <td style={{
                      padding: '4px 4px', fontSize: '0.62rem', color: '#555',
                      textAlign: 'center', fontWeight: 700,
                      borderRight: '1px solid #1e1e1e', background: '#0d0d0d',
                      whiteSpace: 'nowrap',
                    }}>
                      R{round}
                    </td>
                    {Array.from({ length: total_teams }, (_, pIdx) => {
                      const pickNo = (round - 1) * total_teams + (pIdx + 1);
                      const mgrIdx = pickToIdx[pickNo];
                      const mgr    = draft_order[mgrIdx];
                      const c      = mgr != null ? colorFor(mgrIdx) : null;
                      return (
                        <td key={pIdx} style={{
                          padding: '3px 1px', textAlign: 'center',
                          background: c ? c.bg : 'transparent',
                          borderLeft: c
                            ? `1px solid ${c.border}35`
                            : '1px solid #0f0f0f',
                          borderBottom: '1px solid #111',
                        }}>
                          {mgr != null ? (
                            <div>
                              <div style={{
                                fontSize: '0.65rem', fontWeight: 700,
                                color: c.text, lineHeight: 1.2,
                              }}>
                                #{pickNo}
                              </div>
                              <div style={{
                                fontSize: '0.55rem', color: '#bbb',
                                lineHeight: 1.1, letterSpacing: '0.01em',
                              }}>
                                {abbrev(mgr.display_name)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#2a2a2a', fontSize: '0.6rem' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, fontSize: '0.7rem', color: '#3a3a3a', lineHeight: 1.5 }}>
          Snake draft with {ordinal(reversal_round)}-round reversal. R1 ascending, R2–R{reversal_round} descending,
          then alternating from R{reversal_round + 1} onward.
        </div>
      </div>
    </div>
  );
}
